// ============================================================
// ✅ ServicesListe.jsx - Liste des services
// Support multi-tenant : Global + Spécifique
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { 
  Plus, Edit, Trash2, Search, 
  Building2, Globe, ChevronLeft, ChevronRight
} from 'lucide-react';
import ServiceModal from './ServiceModal';
import EditServiceModal from './EditServiceModal';
import DeleteConfirmModal from './DeleteConfirmModal';

export default function ServicesListe() {
  
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  const { user } = useUser();
  const isSuperAdmin = user?.EstSuperAdmin === 1 || user?.EstSuperAdmin === true;

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await api.get('/services', {
        params: { ID_Societe: isSuperAdmin ? null : user?.ID_Societe }
      });
      setServices(res.data.services || []);
    } catch (err) {
      console.error('Erreur chargement services:', err);
    } finally { setLoading(false); }
  };

  const handleEditClick = (s) => { setSelectedService(s); setShowEditModal(true); };
  const handleDeleteClick = (s) => { setServiceToDelete(s); setShowDeleteModal(true); };

  const filteredServices = services.filter(s =>
    s.Nom_Services?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.Nom_Departement?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredServices.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);

  const stats = {
    total: services.length,
    globaux: services.filter(s => s.EstGlobal === 1).length,
    specifiques: services.filter(s => s.EstGlobal === 0).length
  };

  return (
    <div className="p-3 md:p-5 bg-gray-50 min-h-screen">
      
      <div className="mb-3">
        <h2 className="text-base md:text-lg font-bold text-gray-800">🛠️ Services</h2>
        <p className="text-[11px] text-gray-400">
          Gestion des services {user?.SocieteNom ? `• ${user.SocieteNom}` : ''}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4">
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Total</p><p className="text-lg font-bold text-gray-800">{stats.total}</p></div>
            <Building2 size={18} className="text-emerald-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Globaux</p><p className="text-lg font-bold text-purple-600">{stats.globaux}</p></div>
            <Globe size={18} className="text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Spécifiques</p><p className="text-lg font-bold text-blue-600">{stats.specifiques}</p></div>
            <Building2 size={18} className="text-blue-500" />
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between gap-2 mb-3">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
          <input type="text" placeholder="Rechercher..."
            className="pl-7 pr-3 py-1.5 border border-gray-200 rounded-lg w-full sm:w-60 text-xs focus:ring-2 focus:ring-emerald-500"
            value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
        </div>
        <button onClick={() => setShowCreateModal(true)} className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs flex items-center justify-center gap-1 transition shadow-sm">
          <Plus size={12} /> Nouveau service
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="text-[11px] text-gray-500">
                <th className="px-3 py-2 text-left">ID</th>
                <th className="px-3 py-2 text-left">Service</th>
                <th className="px-3 py-2 text-left">Département</th>
                <th className="px-3 py-2 text-center">Postes</th>
                <th className="px-3 py-2 text-center">Portée</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="6" className="px-3 py-8 text-center text-gray-400 text-xs">Chargement...</td></tr>
              ) : currentItems.length === 0 ? (
                <tr><td colSpan="6" className="px-3 py-8 text-center text-gray-400 text-xs">Aucun service</td></tr>
              ) : (
                currentItems.map(s => (
                  <tr key={s.ID_Services} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{s.ID_Services}</td>
                    <td className="px-3 py-2">{s.Nom_Services}</td>
                    <td className="px-3 py-2">{s.Nom_Departement || '-'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700">{s.NbPostes || 0}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {s.EstGlobal === 1 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700"><Globe size={10} /> Global</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700"><Building2 size={10} /> {s.RaisonSociale || s.Sigle || `ID: ${s.ID_Societe}`}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleEditClick(s)} className="p-1 text-blue-500 hover:text-blue-700 transition"><Edit size={14} /></button>
                        <button onClick={() => handleDeleteClick(s)} className="p-1 text-red-500 hover:text-red-700 transition"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-3 py-2 border-t border-gray-100 flex justify-between items-center">
            <p className="text-[10px] text-gray-400">{indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredServices.length)} sur {filteredServices.length}</p>
            <div className="flex gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1 rounded disabled:opacity-40 hover:bg-gray-100"><ChevronLeft size={14} /></button>
              <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1 rounded disabled:opacity-40 hover:bg-gray-100"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      <ServiceModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={fetchServices} />
      <EditServiceModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedService(null); }} onSuccess={fetchServices} service={selectedService} />
      <DeleteConfirmModal isOpen={showDeleteModal} onClose={() => { setShowDeleteModal(false); setServiceToDelete(null); }} onSuccess={fetchServices} service={serviceToDelete} />
    </div>
  );
}